import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card.tsx';
import InitialsAvatar from '@/components/ui/InitialsAvatar.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { PublicSummaryParticipantDto } from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';
import { bookingBillMapper } from '@/features/public/bookingSummary/types/BookingBill.mapper.ts';
import { formatPriceList } from '@/utils/formatters.ts';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';
import { pluralizeYears } from '@/features/dashboard/bookingChanges/utils/plurals.ts';
import { formatDate } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import BillLineRow from './BillLineRow';
import DetailRow from './DetailRow';

interface ParticipantBillTileProps
  extends React.HTMLAttributes<HTMLDivElement> {
  participant: PublicSummaryParticipantDto;
}

/**
 * Kafel uczestnika na podsumowaniu rezerwacji — ten sam układ, który organizator ma na swoim
 * ekranie: zwinięty niesie nazwisko i kwotę, rozwinięty rozpisuje rachunek.
 *
 * Nazwy wyjazdu tu nie ma, choć u organizatora jest: on ogląda uczestnika w kontekście wielu
 * wyjazdów, klient ma jedną rezerwację i jeden wyjazd, który stoi w nagłówku strony.
 */
const ParticipantBillTile = ({
  participant,
  className,
  ...props
}: ParticipantBillTileProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();

  const fullName = `${participant.firstName} ${participant.lastName}`;
  const age = bookingBillMapper.ageOf(participant);
  const sections = bookingBillMapper.toSections(participant.costLines);

  return (
    <Card className={cn('overflow-hidden', className)} {...props}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
      >
        <InitialsAvatar
          initials={`${participant.firstName[0]}${participant.lastName[0]}`}
          tone="plain"
          size="lg"
        />
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-bold text-ink">
            {fullName}
          </span>
          <span className="block text-sm text-neutral-500">
            Uczestnik · {pluralizeYears(age)}
          </span>
        </span>
        <span className="ml-auto flex items-center gap-2.5">
          <span className="font-semibold tabular-nums text-ink">
            {participant.totalPrice.length > 0 ? (
              formatPriceList(participant.totalPrice)
            ) : (
              <EmptyValue />
            )}
          </span>
          <ChevronDown
            aria-hidden
            className={cn(
              'size-4 shrink-0 text-neutral-400 transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </span>
      </button>

      {isOpen && (
        <div
          id={panelId}
          className="flex flex-col gap-4 border-t border-neutral-100 px-4 py-3.5"
        >
          {sections.map((section) => (
            <div key={section.id}>
              <div className={columnLabelClass}>{section.title}</div>
              <div className="mt-1">
                {section.lines.map((line, index) => (
                  <BillLineRow key={`${section.id}-${index}`} line={line} />
                ))}
              </div>
            </div>
          ))}

          <div>
            <div className={columnLabelClass}>Uczestnik</div>
            <DetailRow label="Data urodzenia" className="mt-1">
              {formatDate(participant.birthDate)}
            </DetailRow>
            {participant.insuranceName && (
              <DetailRow label="Ubezpieczenie">
                {participant.insuranceName}
              </DetailRow>
            )}
          </div>

          {participant.formAnswers.length > 0 && (
            <div>
              <div className={columnLabelClass}>Informacje dodatkowe</div>
              <div className="mt-1">
                {participant.formAnswers.map((answer) => (
                  <DetailRow key={answer.label} label={answer.label}>
                    {answer.value}
                  </DetailRow>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default ParticipantBillTile;
